/**
 * AI Office 助手側欄（Sprint 4）· 內嵌 OfficeEditorPage 右側 30%。
 * 對話 → Claude tool-use → 回文字 + 建議動作；使用者按「插入」→ 呼叫 onInsert/onRefineReplace
 * （由 OfficeEditorPage postMessage 給 Collabora）。非串流（單輪 tool_chat）。
 */
import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, Paper, Stack, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import { useMessage } from '../../../../contexts/MessageContext';
import {
  officeChat, reportOfficeChatOutcomes, OfficeAction, OfficeChatMessage, OutcomeReport,
} from '../services/aiOfficeService';
import UndoBar from './UndoBar';

const UNDO_TIMEOUT_MS = 8000;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

interface Props {
  fileObjectId?: number;
  mimeType: string;
  filename: string;
  onInsert: (content: string, style?: string) => void;
  onRefineReplace: (content: string) => void;
  onUndo?: () => void;    // post-b · 取消 auto-apply → Collabora undo
  onClose?: () => void;   // post-a · header × 關閉面板
}

type ActionPhase = 'applying' | 'undoable' | 'kept' | 'cancelled';
interface ChatTurn extends OfficeChatMessage {
  actions?: OfficeAction[];
  usageLogId?: number;
  phase?: ActionPhase;
  deadline?: number;
}

// tool action → 可插入內容 + 顯示標籤 + 是否為「取代選取」
export function actionToInsert(a: OfficeAction): { kind: 'insert' | 'replace'; content: string; style?: string; label: string } {
  const inp = a.input || {};
  const s = (k: string) => (typeof inp[k] === 'string' ? (inp[k] as string) : '');
  const arr = (k: string) => (Array.isArray(inp[k]) ? (inp[k] as unknown[]) : []);
  switch (a.name) {
    case 'refine_selection':
      return { kind: 'replace', content: s('content'), label: '取代選取' };
    case 'build_docx_section':
      return { kind: 'insert', content: [s('title'), '', ...arr('paragraphs').map(String)].join('\n'), label: '插入段落' };
    case 'build_pptx_slide':
      return { kind: 'insert', content: [s('title'), ...arr('bullets').map((b) => `• ${String(b)}`)].join('\n'), label: '插入投影片內容' };
    case 'build_xlsx_table': {
      const headers = arr('headers').map(String).join('\t');
      const rows = arr('rows').map((row) => (Array.isArray(row) ? row.map(String).join('\t') : String(row)));
      return { kind: 'insert', content: [headers, ...rows].join('\n'), label: '插入表格（Tab 分隔）' };
    }
    case 'insert_text':
    default:
      return { kind: 'insert', content: s('content'), style: s('style') || 'default', label: '插入' };
  }
}

const AiOfficePanel: React.FC<Props> = ({ fileObjectId, mimeType, filename, onInsert, onRefineReplace, onUndo, onClose }) => {
  const { t } = useTranslation();
  const tt = (k: string, d: string) => t(`aiOffice.${k}`, d);
  const { showApiError } = useMessage();
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);   // busy = 送 chat 或 auto-apply 中 → 禁再送
  const scrollRef = useRef<HTMLDivElement>(null);
  const turnsRef = useRef<ChatTurn[]>([]);
  turnsRef.current = turns;

  const _setPhase = (usageLogId: number, patch: Partial<ChatTurn>) =>
    setTurns((p) => p.map((x) => (x.usageLogId === usageLogId ? { ...x, ...patch } : x)));

  // route 切走 / unmount：仍在 undoable 的視為離開（route_left），補回報
  useEffect(() => () => {
    turnsRef.current.filter((x) => x.phase === 'undoable' && x.usageLogId).forEach((x) => {
      reportOfficeChatOutcomes(x.usageLogId!, (x.actions || []).map((a, i) => ({
        action_index: i, tool_name: a.name, outcome: 'route_left',
      })));
    });
  }, []);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    const history: OfficeChatMessage[] = turns.map((x) => ({ role: x.role, content: x.content }));
    const userTurn: ChatTurn = { role: 'user', content: text };
    setTurns((p) => [...p, userTurn]);
    setInput('');
    setBusy(true);
    try {
      const resp = await officeChat({
        messages: [...history, { role: 'user', content: text }],
        mime_type: mimeType, filename, file_object_id: fileObjectId,
      });
      const actions = resp.actions || [];
      setTurns((p) => [...p, {
        role: 'assistant', content: resp.text || tt('noText', '（已產生建議動作）'),
        actions, usageLogId: resp.usage_log_id, phase: actions.length ? 'applying' : undefined,
      }]);
      setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }), 50);

      // 4-post-b · auto-apply：立即套用每個動作 + 回報 outcome
      if (actions.length) {
        const outcomes: OutcomeReport[] = [];
        for (let i = 0; i < actions.length; i++) {
          const a = actions[i];
          try {
            const r = actionToInsert(a);
            if (!r.content) { outcomes.push({ action_index: i, tool_name: a.name, outcome: 'postmessage_sent' }); continue; }
            if (r.kind === 'replace') onRefineReplace(r.content);
            else onInsert(r.content, r.style);
            outcomes.push({ action_index: i, tool_name: a.name, outcome: 'postmessage_sent' });
            await sleep(150);
          } catch (e) {
            outcomes.push({ action_index: i, tool_name: a.name, outcome: 'postmessage_failed', detail: { error: String(e) } });
          }
        }
        reportOfficeChatOutcomes(resp.usage_log_id, outcomes);
        _setPhase(resp.usage_log_id, { phase: 'undoable', deadline: Date.now() + UNDO_TIMEOUT_MS });
      }
    } catch (err) {
      showApiError(err);
      setTurns((p) => p.filter((x) => x !== userTurn));   // 回捲失敗的 user turn
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = (turn: ChatTurn) => {
    if (turn.phase !== 'undoable' || !turn.usageLogId) return;
    (turn.actions || []).forEach(() => onUndo?.());   // 每個動作 undo 一次
    reportOfficeChatOutcomes(turn.usageLogId, (turn.actions || []).map((a, i) => ({
      action_index: i, tool_name: a.name, outcome: 'cancelled',
    })));
    _setPhase(turn.usageLogId, { phase: 'cancelled' });
  };

  const handleKeep = (turn: ChatTurn, kind: 'kept_by_user' | 'kept_by_timeout') => {
    if (turn.phase !== 'undoable' || !turn.usageLogId) return;
    reportOfficeChatOutcomes(turn.usageLogId, (turn.actions || []).map((a, i) => ({
      action_index: i, tool_name: a.name, outcome: kind,
    })));
    _setPhase(turn.usageLogId, { phase: 'kept' });
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 1.5, borderBottom: '1px solid #eee', display: 'flex', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1"><strong>🤖 {tt('title', 'AI Office 助手')}</strong></Typography>
          <Typography variant="caption" color="text.secondary" noWrap display="block">{filename}</Typography>
        </Box>
        {onClose && (
          <IconButton size="small" onClick={onClose} aria-label={t('common.close', '關閉')}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Box ref={scrollRef} sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>
        {turns.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {tt('hint', '試試：「幫我寫一份會議記錄範本」、「把選取文字翻成英文」')}
          </Typography>
        )}
        {turns.map((m, i) => (
          <Paper key={i} variant="outlined"
            sx={{ p: 1, mb: 1, bgcolor: m.role === 'user' ? '#f0f7ff' : '#fafafa' }}>
            <Typography variant="caption" color="text.secondary">
              {m.role === 'user' ? tt('you', '你') : tt('ai', 'AI')}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.content}</Typography>
            {/* 4-post-b · auto-apply 狀態 */}
            {m.phase === 'applying' && (
              <Typography variant="caption" color="text.secondary">⏳ {tt('applying', '套用中…')}</Typography>
            )}
            {m.phase === 'undoable' && m.deadline && (
              <UndoBar deadline={m.deadline}
                onCancel={() => handleCancel(m)}
                onKeep={(kind) => handleKeep(m, kind)} />
            )}
            {m.phase === 'kept' && (
              <Typography variant="caption" color="success.main">✓ {tt('applied', '已套用')}</Typography>
            )}
            {m.phase === 'cancelled' && (
              <Typography variant="caption" color="text.secondary">↩ {tt('cancelled', '已取消')}</Typography>
            )}
          </Paper>
        ))}
        {busy && <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={14} /><Typography variant="caption">{tt('thinking', 'AI 思考中…')}</Typography></Stack>}
      </Box>

      <Box sx={{ p: 1, borderTop: '1px solid #eee' }}>
        <Stack direction="row" spacing={1}>
          <TextField size="small" fullWidth multiline maxRows={4} value={input}
            placeholder={tt('placeholder', '請 AI 幫你…')}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} />
          <Button variant="contained" disabled={busy || !input.trim()} onClick={send}>{tt('send', '送出')}</Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default AiOfficePanel;
